/**
 * SocialLinks.tsx — Social Media Links
 * 
 * Data-driven — reads links from admin-configured data.links prop.
 * Each link should have { platform, url }. Supports common platforms
 * with SVG icons.
 */
import React from 'react';

interface SocialLink {
  platform: string;
  url: string;
}

interface SocialLinksProps {
  data: {
    links?: SocialLink[];
    size?: 'sm' | 'md' | 'lg';
    variant?: string;
    content?: { variant?: string };
    [key: string]: unknown;
  };
  styles?: string;
  variant?: string;
  /** Carries the synced CMS blocks (globalContext.cmsBlocks) so links can be
   *  sourced from a `social_media_footer` block. */
  context?: { cmsBlocks?: Array<{ identifier?: string; content?: string }>; [key: string]: unknown };
}

/**
 * Parse social links from the `social_media_footer` CMS block. Sanitizer-safe
 * plain-text format, one per line:  `platform | https://…`  (also accepts a
 * space or colon separator). Tags/entities are stripped first so it survives the
 * CMS HTML sanitiser. Unknown platforms still render (first-letter fallback).
 */
function parseSocialBlock(html?: string): SocialLink[] {
  if (!html) return [];
  const text = html
    .replace(/<\s*br\s*\/?>/gi, '\n')
    .replace(/<\/(?:p|div|li|h[1-6])>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&#0?39;|&apos;/gi, "'");
  const out: SocialLink[] = [];
  for (const raw of text.split('\n')) {
    const line = raw.trim();
    if (!line) continue;
    const m = line.match(/^([a-z0-9][a-z0-9 ]*?)\s*[|:]\s*(\S+)$/i) ?? line.match(/^([a-z0-9]+)\s+(\S+)$/i);
    if (!m) continue;
    const platform = m[1].trim().toLowerCase();
    const url = m[2].trim();
    if (platform && /^https?:\/\//i.test(url)) out.push({ platform, url });
  }
  return out;
}

const PLATFORM_ICONS: Record<string, string> = {
  facebook: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>',
  twitter: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>',
  x: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>',
  instagram: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12s.014 3.668.072 4.948c.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24s3.668-.014 4.948-.072c4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>',
  linkedin: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 11-.002-4.125 2.062 2.062 0 01.002 4.125zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>',
  youtube: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>',
  tiktok: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>',
  pinterest: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12.017 24c6.624 0 11.99-5.367 11.99-11.988C24.007 5.367 18.641 0 12.017 0z"/></svg>',
};

export const SocialLinks: React.FC<SocialLinksProps> = ({ data, styles, variant, context }) => {
  const legacyVariant = variant ?? data?.variant;
  const isBentoDark = legacyVariant === 'bento-dark';
  const { size = 'md' } = data ?? {};

  // Resolve links in priority order: editor (data.links) → synced CMS block
  // `social_media_footer` → nothing. So a buyer can set links in ONE Magento CMS
  // block (synced, no rebuild, no page-editor needed); explicit editor links
  // still win if present.
  const editorLinks = (data?.links ?? []).filter((l) => Boolean(l?.platform && l?.url));
  const blockContent = context?.cmsBlocks?.find((b) => b.identifier === 'social_media_footer')?.content;
  const links: SocialLink[] = editorLinks.length > 0 ? editorLinks : parseSocialBlock(blockContent);

  // Variant: icon-row (default) | icon-row-pills | icon-grid
  const newVariantRaw = data?.content?.variant ?? (typeof legacyVariant === 'string' ? legacyVariant : undefined) ?? 'icon-row';
  const layoutVariant: 'icon-row' | 'icon-row-pills' | 'icon-grid' =
    ['icon-row','icon-row-pills','icon-grid'].includes(newVariantRaw)
      ? newVariantRaw as 'icon-row' | 'icon-row-pills' | 'icon-grid'
      : 'icon-row';

  // If no links configured, don't render
  if (links.length === 0) return null;

  const iconSize = size === 'sm' ? 'w-4 h-4' : size === 'lg' ? 'w-6 h-6' : 'w-5 h-5';
  const btnSize = size === 'sm' ? 'w-8 h-8' : size === 'lg' ? 'w-11 h-11' : 'w-9 h-9';

  // Variant-aware wrapper + button styling.
  const wrapperClass = layoutVariant === 'icon-grid'
    ? 'grid grid-cols-4 sm:grid-cols-6 gap-2'   // wraps to 2 rows on narrow viewports
    : 'flex items-center gap-2 flex-wrap';
  const pillStyle = layoutVariant === 'icon-row-pills' ? {
    border: 'var(--style-chip-border, 1px solid var(--glass-border))',
    borderRadius: 'var(--style-pill-radius, 999px)',
    background: 'var(--style-chip-bg, transparent)',
  } : {};

  return (
    <div className={`${wrapperClass} ${styles ?? ''}`}>
      {links.map((link) => {
        const iconSvg = PLATFORM_ICONS[link.platform.toLowerCase()];
        // 2026-05-21 audit fix — Tailwind JIT mis-parses nested var-fallback
        // chains like `text-[var(--color-footer-link,var(--text-muted,#fff))]`,
        // so the chip silently rendered in the muted grey fallback even
        // though --color-footer-link was a clean #FFFFFF in dark mode.
        // Pull the colour from inline style instead — the tokens themselves
        // are already mode-aware via the theme injector, so this is just
        // surfacing them correctly.
        const linkInlineStyle: React.CSSProperties = {
          textDecoration: 'none',
          color: 'var(--color-footer-link)',
          ...pillStyle,
        };
        return (
          <a
            key={link.platform}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className={`${btnSize} flex items-center justify-center transition-all footer-social-link ${isBentoDark
                ? 'border border-[color-mix(in_srgb,var(--glass-border)_30%,transparent)]'
                : ''
              }`}
            aria-label={link.platform}
            style={linkInlineStyle}
          >
            {iconSvg ? (
              <span className={iconSize} style={{ color: 'currentColor' }} dangerouslySetInnerHTML={{ __html: iconSvg }} />
            ) : (
              <span className="text-xs font-bold uppercase">{link.platform[0]}</span>
            )}
          </a>
        );
      })}
      <style>{`.footer-social-link:hover{color:var(--color-footer-link-hover) !important;}`}</style>
    </div>
  );
};

export default SocialLinks;
